// ABOUTME: Hook returning { bg, bgTransparent, color } for a melding type — extracts the dominant saturated colour from the SVG logo via canvas, falls back to hash-based colours.
// ABOUTME: HSL-tuned per theme mode (lighter shades for dark mode, darker for light mode) so badges stay readable on either surface.

import { useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { generateColorFromTitle, generateIconColorFromTitle } from "../../../theme/TabView3/IconColorGenerator";
import { tokens } from "../../../../theme";
import { findMeldingType, getMeldingTypeLogo } from "./meldingTypes";
import { getSeverityPalette } from "./noticeSeverity";

const cache = new Map();
const inFlight = new Set();
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4;
        }
        h *= 60;
    }
    return [h, s * 100, l * 100];
};

const sampleSvgString = (svgString) => new Promise((resolve) => {
    const match = svgString.match(/<svg[\s\S]*?<\/svg>/i);
    if (!match) { resolve(null); return; }
    const svg = match[0];
    let url;
    try {
        const blob = new Blob([svg], { type: "image/svg+xml" });
        url = URL.createObjectURL(blob);
    } catch {
        resolve(null);
        return;
    }
    const img = new Image();
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch { /* noop */ } };
    img.onload = () => {
        try {
            const canvas = document.createElement("canvas");
            canvas.width = 32;
            canvas.height = 32;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, 32, 32);
            const data = ctx.getImageData(0, 0, 32, 32).data;
            let r = 0, g = 0, b = 0, count = 0;
            for (let i = 0; i < data.length; i += 4) {
                const a = data[i + 3];
                if (a < 200) continue;
                const rr = data[i], gg = data[i + 1], bb = data[i + 2];
                if (rr > 235 && gg > 235 && bb > 235) continue;
                if (rr < 20 && gg < 20 && bb < 20) continue;
                const mx = Math.max(rr, gg, bb);
                const mn = Math.min(rr, gg, bb);
                if (mx - mn < 35) continue;
                r += rr; g += gg; b += bb; count++;
            }
            cleanup();
            if (count === 0) { resolve(null); return; }
            resolve([Math.round(r / count), Math.round(g / count), Math.round(b / count)]);
        } catch {
            cleanup();
            resolve(null);
        }
    };
    img.onerror = () => { cleanup(); resolve(null); };
    img.src = url;
});

const trigger = (typeId) => {
    if (!typeId || cache.has(typeId) || inFlight.has(typeId)) return;
    const logoElement = getMeldingTypeLogo(typeId, 64);
    if (!logoElement) {
        cache.set(typeId, null);
        return;
    }
    inFlight.add(typeId);
    let svgString = null;
    try { svgString = renderToStaticMarkup(logoElement); }
    catch { /* noop */ }
    if (!svgString) {
        inFlight.delete(typeId);
        cache.set(typeId, null);
        notify();
        return;
    }
    sampleSvgString(svgString).then((rgb) => {
        cache.set(typeId, rgb);
        inFlight.delete(typeId);
        notify();
    });
};

export const useTypeColors = (typeId, mode = "light", severity = null) => {
    const [, force] = useState(0);

    useEffect(() => {
        if (!typeId) return;
        trigger(typeId);
        const fn = () => force((n) => n + 1);
        listeners.add(fn);
        return () => { listeners.delete(fn); };
    }, [typeId]);

    // Severity overrides the type-derived colours so users see at-a-glance how serious a notice is.
    if (severity) {
        const colors = tokens(mode);
        const palette = getSeverityPalette(severity, mode, colors);
        if (palette) {
            if (mode === "dark") {
                return {
                    bg: palette[400],
                    bgTransparent: palette[100],
                    labelBg: `color-mix(in srgb, ${palette[400]} 55%, black)`,
                    softBg: `color-mix(in srgb, ${palette[400]} 35%, white)`,
                    color: palette[400],
                };
            }
            return {
                bg: palette[400],
                bgTransparent: palette[100],
                labelBg: palette[400],
                softBg: `color-mix(in srgb, ${palette[400]} 10%, white)`,
                color: palette[400],
            };
        }
    }

    const entry = findMeldingType(typeId);
    const label = entry ? entry.label : (typeId || "");

    const rgb = cache.get(typeId);
    if (Array.isArray(rgb)) {
        const [h, s] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
        const sat = Math.max(s, 55);
        if (mode === "dark") {
            return {
                bg: `hsl(${h}, ${sat}%, 30%)`,
                bgTransparent: `hsla(${h}, ${sat}%, 65%, 0.18)`,
                labelBg: `hsl(${h}, ${sat}%, 30%)`,
                softBg: `hsl(${h}, ${sat}%, 85%)`,
                color: `hsl(${h}, ${Math.min(sat + 10, 90)}%, 78%)`,
            };
        }
        return {
            bg: `hsl(${h}, ${sat}%, 88%)`,
            bgTransparent: `hsla(${h}, ${sat}%, 50%, 0.14)`,
            labelBg: `hsl(${h}, ${Math.min(sat + 10, 90)}%, 32%)`,
            softBg: `hsl(${h}, ${sat}%, 88%)`,
            color: `hsl(${h}, ${Math.min(sat + 10, 90)}%, 32%)`,
        };
    }

    return {
        bg: generateColorFromTitle(label, mode, 93),
        bgTransparent: generateColorFromTitle(label, mode, 75),
        labelBg: generateIconColorFromTitle(label, mode),
        softBg: generateColorFromTitle(label, mode, 88),
        color: generateIconColorFromTitle(label, mode),
    };
};
